import { memo, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { IExtendedSearchItem } from "src/api/client/extended-search.api";
import { LoadingSearchResult } from "src/components/pages/search/components/card-result/loading-search-result";
import { SearchResultCard } from "src/components/pages/search/components/card-result/search-result-card";
import useViewportSpy from "src/hooks/dom/use-viewport-spy";

interface ISearchGridResultsProps {
    cards: ReadonlyArray<IExtendedSearchItem> | null;
    currency?: string;
    showLoadingSkeleton: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
}

const SENTINEL_VIEWPORT_THRESHOLD = [0.1, 0.5, 0.7];

const SearchGridResultsComponent = ({
    cards,
    currency,
    showLoadingSkeleton,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
}: ISearchGridResultsProps) => {
    const sentinelRef = useRef<HTMLDivElement>(null);

    const onIntersecting = useCallback(
        (isIntersecting: boolean) => {
            if (isIntersecting && hasNextPage && !isFetchingNextPage) {
                onLoadMore();
            }
        },
        [hasNextPage, isFetchingNextPage, onLoadMore]
    );

    useViewportSpy(sentinelRef, { threshold: SENTINEL_VIEWPORT_THRESHOLD, onIntersecting });

    return (
        <Box sx={{ position: "relative", zIndex: 0 }}>
            <Box
                component={showLoadingSkeleton ? "div" : "ul"}
                role={showLoadingSkeleton ? undefined : "list"}
                sx={{
                    listStyleType: "none",
                    display: "grid",
                    gridTemplateColumns: ["minmax(0, 1fr)", "repeat(2, minmax(0, 1fr))", "repeat(3, minmax(0, 1fr))"],
                    gap: 2,
                    m: 0,
                    p: 0,
                }}
            >
                {showLoadingSkeleton ? (
                    <LoadingSearchResult />
                ) : (
                    cards?.map(({ wikifolioId, symbol, wikifolioShortDescription, trader, figures, isOnWatchlist }) => (
                        <Box component="li" key={wikifolioId + symbol} sx={{ listStyleType: "none" }}>
                            <SearchResultCard
                                currency={currency}
                                wikifolioId={wikifolioId}
                                symbol={symbol}
                                wikifolioShortDescription={wikifolioShortDescription}
                                trader={trader}
                                figures={figures}
                                isOnWatchlist={isOnWatchlist}
                            />
                        </Box>
                    ))
                )}
            </Box>
            <Box
                ref={sentinelRef}
                sx={{
                    position: "absolute",
                    display: showLoadingSkeleton || !hasNextPage ? "none" : "initial",
                    bottom: -40,
                    left: 0,
                    right: 0,
                    zIndex: -1,
                    height: ["2200px", "1800px"],
                }}
            ></Box>
        </Box>
    );
};

export const SearchGridResults = memo(SearchGridResultsComponent);
